import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Search, FileCheck, ChevronLeft, ChevronRight } from 'lucide-react';

const ProcessSection = () => {
  const [current, setCurrent] = useState(0);

  const steps = [
    {
      icon: MessageCircle,
      title: 'Brand Introduction',
      description: 'Tell us about your business, your vision, and the trademark you want to secure. The more we know, the better we can protect what matters to you.'
    },
    {
      icon: Search,
      title: 'Trademark Availability Check',
      description: 'Our legal experts run a detailed search across federal databases to confirm your trademark is unique and ready for registration.'
    },
    {
      icon: FileCheck,
      title: 'Filing Made Easy',
      description: 'We draft, review, and file your trademark application with the USPTO—ensuring accuracy and increasing your chances of quick approval.'
    }
  ];

  const next = useCallback(() => setCurrent(p => (p + 1) % steps.length), [steps.length]);

  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <div className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            Process
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
            Our Simple 3-Step Trademark Protection Process
          </h2>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="relative group">
              <div className="bg-white rounded-xl p-8 h-full border-2 border-gray-200 transition-all duration-300 hover:border-[#FF035B] hover:shadow-[0_10px_40px_rgba(255,3,91,0.3)] hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-lg flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                  <step.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-black mb-4 transition-colors duration-300 group-hover:text-[#FF035B]">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                  <div className="w-8 h-0.5 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E]"></div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Mobile Auto-Slider */}
        <div className="md:hidden">
          <div className="overflow-hidden">
            <div className="transition-transform duration-500 ease-in-out flex" style={{ transform: `translateX(-${current * 100}%)` }}>
              {steps.map((step, index) => (
                <div key={index} className="w-full flex-shrink-0 px-2">
                  <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-lg flex items-center justify-center mb-4">
                      <step.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-black mb-3">{step.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center items-center gap-4 mt-4">
            <button onClick={() => setCurrent(p => (p - 1 + steps.length) % steps.length)} className="w-8 h-8 rounded-full bg-[#FF035B] text-white flex items-center justify-center" aria-label="Previous">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-2">
              {steps.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)} className={`h-2 rounded-full transition-all ${current === i ? 'bg-[#FF035B] w-6' : 'bg-gray-300 w-2'}`} aria-label={`Step ${i + 1}`} />
              ))}
            </div>
            <button onClick={next} className="w-8 h-8 rounded-full bg-[#FF035B] text-white flex items-center justify-center" aria-label="Next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessSection;
